/**
 * Authentication types for streaming apps
 */
export enum AuthenticationType {
  NONE = 'NONE',
  OPENID = 'OPENID',
  CUSTOM = 'CUSTOM',
}

/**
 * Represents an application version
 */
export interface StreamingAppVersion {
  id: string;
  name: string;
  releaseDate: string;
  status: 'stable' | 'beta' | 'deprecated';
}

/**
 * Represents a streaming application
 */
export interface StreamingApp {
  id: string;
  title: string;
  description: string;
  division: string;
  category: string;
  icon: string;
  currentVersion: StreamingAppVersion;
  authType: AuthenticationType;
  serverAddress?: string;
  serverPort?: number;
  versions: StreamingAppVersion[];
}

/**
 * Stream session status enum
 */
export enum StreamSessionStatus {
  INITIALIZING = 'initializing',
  CONNECTING = 'connecting',
  ACTIVE = 'active',
  FAILED = 'failed',
  TERMINATED = 'terminated',
}

/**
 * Stream session interface
 */
export interface StreamSession {
  id: string;
  status: StreamSessionStatus;
  appId: string;
  startTime: Date;
  endTime?: Date;
  resourceUsage?: {
    cpu: number;
    memory: number;
    gpu: number;
  };
  region?: string;
  connectionQuality?: 'excellent' | 'good' | 'fair' | 'poor';
  userInfo?: {
    userId: string;
    userName: string;
  };
}

/**
 * Stream statistics
 */
export interface StreamStatistics {
  latency: number;
  packetLoss: number;
  bitrate: number;
  frameRate: number;
  resolution: {
    width: number;
    height: number;
  };
}

/**
 * Mock stream statistics
 */
export const MOCK_STREAM_STATS: StreamStatistics = {
  latency: 28,
  packetLoss: 0.5,
  bitrate: 15.7,
  frameRate: 60,
  resolution: {
    width: 1920,
    height: 1080,
  },
};

/**
 * Gets stream statistics (mock implementation)
 */
export async function getStreamStatistics(
  sessionId: string
): Promise<StreamStatistics> {
  // Simulate API call delay
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 300);
  });

  // Return mock data with slight variations
  return {
    ...MOCK_STREAM_STATS,
    latency: Math.floor(25 + Math.random() * 10),
    packetLoss: parseFloat((0.1 + Math.random() * 0.8).toFixed(2)),
    frameRate: Math.floor(55 + Math.random() * 10),
  };
}

/**
 * Interface for paginated session response
 */
export interface SessionPage {
  items: StreamSession[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalSize: number;
}

/**
 * Parameters for getting sessions
 */
export interface GetSessionsParams {
  appId?: string;
  status?: string; // 'active' or 'stopped'
}

/**
 * Gets stream sessions from the backend
 * Calls GET /api/v1/sessions/?status=X&app_id=X
 */
export async function getSessions(
  params: GetSessionsParams
): Promise<SessionPage> {
  console.log('=== GET SESSIONS ===');
  console.log('Parameters:', params);

  const { appId, status } = params;

  const queryParams = new URLSearchParams();
  if (status) {
    queryParams.set('status', status);
  }
  if (appId) {
    queryParams.set('app_id', appId);
  }

  const url = `/sessions/?${queryParams.toString()}`;
  console.log('GET URL:', url);

  try {
    // Import pepApiRequest dynamically to avoid circular dependencies
    const { pepApiRequest } = await import('./pepApi');
    const { RequestMethod } = await import('./types');

    console.log('Making API request...');
    const response = await pepApiRequest({
      method: RequestMethod.Get,
      url,
      useSessionProxy: true,
      credentials: 'include',
    });

    console.log('API Response:', response);

    // Map the API response to SessionPage
    const sessionPage: SessionPage = {
      items:
        response.items?.map((item: any) => ({
          id: item.id,
          status: mapStatusToEnum(item.status),
          appId: item.appId,
          startTime: new Date(item.startDate || item.createdAt),
          endTime: item.endDate ? new Date(item.endDate) : undefined,
          userInfo: {
            userId: item.userId || '',
            userName: item.userName || '',
          },
        })) || [],
      page: response.page || 1,
      pageSize: response.pageSize || 0,
      totalPages: response.totalPages || 1,
      totalSize: response.totalSize || 0,
    };

    console.log('Mapped session page:', sessionPage);
    return sessionPage;
  } catch (error) {
    console.error('=== GET SESSIONS ERROR ===');
    console.error('Error:', error);
    throw new Error(
      `Failed to get sessions: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Terminates a stream session
 * Calls DELETE /api/v1/sessions/{sessionId}
 */
export async function terminateStreamSession(sessionId: string): Promise<void> {
  console.log('=== TERMINATE STREAM SESSION ===');
  console.log('Session ID:', sessionId);

  const url = `/sessions/${sessionId}`;
  console.log('DELETE URL:', url);

  try {
    const { pepApiRequest } = await import('./pepApi');
    const { RequestMethod } = await import('./types');

    await pepApiRequest({
      method: RequestMethod.Delete,
      url,
      credentials: 'include',
    });

    console.log(`Session ${sessionId} terminated successfully`);
  } catch (error) {
    console.error('=== TERMINATE SESSION ERROR ===');
    console.error('Error:', error);
    throw new Error(
      `Failed to terminate session: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}
/**
 * Parameters for starting a new stream session
 */
export interface StartSessionParams {
  appId: string;
}

/**
 * Starts a new stream session
 * Calls POST /api/v1/sessions/?app_id={appId}
 * Simple format matching sample project
 *
 * Response includes session data with cookies that will be used for streaming connection
 */
export async function startStreamSession(
  params: StartSessionParams
): Promise<StreamSession> {
  const { appId } = params;

  console.log('=== START STREAM SESSION ===');
  console.log('Parameters:', params);

  // Build the URL with query parameters (simple format)
  const queryParams = new URLSearchParams({
    app_id: appId,
  });

  const url = `/sessions/?${queryParams.toString()}`;
  console.log('POST URL:', url);

  try {
    // Import pepApiRequest dynamically to avoid circular dependencies
    const { pepApiRequest } = await import('./pepApi');
    const { RequestMethod } = await import('./types');

    console.log('Making POST API request to create session...');

    // Make the API call with credentials to ensure cookies are sent/received
    // The backend will set cookies in the response which are needed for streaming
    const response = await pepApiRequest({
      method: RequestMethod.Post,
      url,
      useSessionProxy: true,
      credentials: 'include', // Critical: ensures cookies are stored
    });

    console.log('✅ Session created successfully');
    console.log('API Response:', response);

    // Map the API response to StreamSession
    // Response structure matches your backend API
    const sessionResult: StreamSession = {
      id: response.id,
      status: mapStatusToEnum(response.status),
      appId: response.appId,
      startTime: new Date(response.startDate || response.createdAt),
      endTime: response.endDate ? new Date(response.endDate) : undefined,
      userInfo: {
        userId: response.userId || 'External',
        userName: response.userName || 'External',
      },
    };

    console.log('Mapped session result:', sessionResult);
    console.log('🍪 Cookies should now be set for streaming connection');

    return sessionResult;
  } catch (error) {
    console.error('=== START STREAM SESSION ERROR ===');
    console.error('Error:', error);
    throw new Error(
      `Failed to start streaming session: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Helper function to map API status strings to StreamSessionStatus enum
 */
function mapStatusToEnum(status: string): StreamSessionStatus {
  const statusUpper = status.toUpperCase();
  switch (statusUpper) {
    case 'ACTIVE':
      return StreamSessionStatus.ACTIVE;
    case 'CONNECTING':
      return StreamSessionStatus.CONNECTING;
    case 'INITIALIZING':
    case 'IDLE':
      return StreamSessionStatus.INITIALIZING;
    case 'FAILED':
      return StreamSessionStatus.FAILED;
    case 'TERMINATED':
    case 'STOPPED':
      return StreamSessionStatus.TERMINATED;
    default:
      return StreamSessionStatus.INITIALIZING;
  }
}
